import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent } from "react";
import { client } from "@/lib/api";
import { RivetsApiError, type AttachmentMediaType, type AttachmentRef } from "@rivets/core";
import { filesFromTransfer, intakeAttachments, transferHasFiles } from "./attachments";

export interface PendingAttachment {
  /** Local identity — the server id only exists once the upload lands. */
  key: string;
  file: File;
  mediaType: AttachmentMediaType;
  /** Object URL, revoked on removal and on unmount. */
  previewUrl: string;
  status: "uploading" | "ready" | "error";
  ref?: AttachmentRef;
  error?: string;
}

export interface AttachmentsController {
  items: PendingAttachment[];
  /** Refs for finished uploads, in tray order. */
  refs: AttachmentRef[];
  uploading: boolean;
  failed: boolean;
  error?: string;
  dragging: boolean;
  disabled: boolean;
  addFiles: (files: readonly File[]) => void;
  remove: (key: string) => void;
  retry: (key: string) => void;
  clear: () => void;
  dismissError: () => void;
  /** Resolves once nothing is in flight; returns the refs that made it. */
  settle: () => Promise<AttachmentRef[]>;
  handlePaste: (event: ClipboardEvent<HTMLElement>) => void;
  dropZoneProps: {
    onDragEnter: (event: DragEvent<HTMLElement>) => void;
    onDragOver: (event: DragEvent<HTMLElement>) => void;
    onDragLeave: (event: DragEvent<HTMLElement>) => void;
    onDrop: (event: DragEvent<HTMLElement>) => void;
  };
}

/**
 * Composer-side attachment state: local previews first, uploads in the
 * background, refs collected for the eventual send. Uploads start the moment a
 * file lands so that pressing send almost never waits; when it does, `settle()`
 * lets the caller auto-wait instead of rejecting the click.
 */
export function useAttachments({
  orgId,
  disabled = false,
  disabledHint,
  initialItems,
  keepOnUnmount,
}: {
  orgId: string;
  disabled?: boolean;
  disabledHint?: string;
  /** A tray handed back by an earlier composer for the same surface. Uploads it had in flight restart. */
  initialItems?: PendingAttachment[];
  /** Offered the tray on unmount; returning true takes ownership of its preview URLs. */
  keepOnUnmount?: (items: PendingAttachment[]) => boolean;
}): AttachmentsController {
  const [items, setItemsState] = useState<PendingAttachment[]>(() => initialItems ?? []);
  const [error, setError] = useState<string | undefined>(undefined);
  const [dragging, setDragging] = useState(false);
  const itemsRef = useRef<PendingAttachment[]>(items);
  const keepOnUnmountRef = useRef(keepOnUnmount);
  keepOnUnmountRef.current = keepOnUnmount;
  const inFlight = useRef(new Map<string, Promise<void>>());
  const dragDepth = useRef(0);

  const setItems = useCallback((update: (current: PendingAttachment[]) => PendingAttachment[]) => {
    itemsRef.current = update(itemsRef.current);
    setItemsState(itemsRef.current);
  }, []);

  // Object URLs outlive React's render cycle, so they are released explicitly
  // on removal and once more on unmount for whatever is still in the tray —
  // unless someone keeps the tray for a later composer.
  useEffect(() => () => {
    if (keepOnUnmountRef.current?.(itemsRef.current)) return;
    for (const item of itemsRef.current) URL.revokeObjectURL(item.previewUrl);
  }, []);

  const upload = useCallback((key: string) => {
    const target = itemsRef.current.find((item) => item.key === key);
    if (!target) return;
    const promise = (async () => {
      try {
        const dimensions = target.mediaType.startsWith("image/")
          ? await measureImage(target.previewUrl)
          : undefined;
        const ref = await client.uploadAttachment(
          orgId,
          target.file,
          target.mediaType,
          target.file.name || undefined,
          dimensions,
        );
        setItems((current) => current.map((item) => (
          item.key === key ? { ...item, status: "ready", ref, error: undefined } : item
        )));
      } catch (cause) {
        setItems((current) => current.map((item) => (
          item.key === key ? { ...item, status: "error", error: uploadErrorText(cause) } : item
        )));
      } finally {
        inFlight.current.delete(key);
      }
    })();
    inFlight.current.set(key, promise);
  }, [orgId, setItems]);

  // An upload cut off by the previous composer unmounting never reported back, so it runs again.
  useEffect(() => {
    for (const item of itemsRef.current) if (item.status === "uploading" && !inFlight.current.has(item.key)) upload(item.key);
  }, []);

  const addFiles = useCallback((files: readonly File[]) => {
    if (files.length === 0) return;
    if (disabled) {
      setError(disabledHint ?? "Files cannot be attached here.");
      return;
    }
    const { accepted, rejection } = intakeAttachments(files, itemsRef.current.length);
    setError(rejection);
    if (accepted.length === 0) return;
    const added = accepted.map(({ file, mediaType }) => ({
      key: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      file,
      mediaType,
      previewUrl: URL.createObjectURL(file),
      status: "uploading" as const,
    }));
    setItems((current) => current.concat(added));
    for (const item of added) upload(item.key);
  }, [disabled, disabledHint, setItems, upload]);

  const remove = useCallback((key: string) => {
    const target = itemsRef.current.find((item) => item.key === key);
    if (target) URL.revokeObjectURL(target.previewUrl);
    inFlight.current.delete(key);
    setItems((current) => current.filter((item) => item.key !== key));
  }, [setItems]);

  const retry = useCallback((key: string) => {
    setItems((current) => current.map((item) => (
      item.key === key ? { ...item, status: "uploading", error: undefined } : item
    )));
    upload(key);
  }, [setItems, upload]);

  const clear = useCallback(() => {
    for (const item of itemsRef.current) URL.revokeObjectURL(item.previewUrl);
    inFlight.current.clear();
    setError(undefined);
    setItems(() => []);
  }, [setItems]);

  const settle = useCallback(async () => {
    // A retry started while we were waiting registers a fresh promise, so the
    // map is drained rather than snapshotted once.
    while (inFlight.current.size > 0) {
      await Promise.allSettled(Array.from(inFlight.current.values()));
    }
    return readyRefs(itemsRef.current);
  }, []);

  const handlePaste = useCallback((event: ClipboardEvent<HTMLElement>) => {
    const files = filesFromTransfer(event.clipboardData);
    if (files.length === 0) return;
    event.preventDefault();
    addFiles(files);
  }, [addFiles]);

  const dropZoneProps = useMemo(() => ({
    onDragEnter: (event: DragEvent<HTMLElement>) => {
      if (!transferHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      dragDepth.current += 1;
      if (!disabled) setDragging(true);
    },
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!transferHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = disabled ? "none" : "copy";
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      if (!transferHasFiles(event.dataTransfer)) return;
      // Drag events fire as the pointer crosses children (including the
      // textarea), but those are still inside the composer drop target. Only
      // clear the treatment once the pointer truly leaves the whole surface.
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
      dragDepth.current = 0;
      setDragging(false);
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      if (!transferHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      addFiles(filesFromTransfer(event.dataTransfer));
    },
  }), [addFiles, disabled]);

  const refs = useMemo(() => readyRefs(items), [items]);

  return {
    items,
    refs,
    uploading: items.some((item) => item.status === "uploading"),
    failed: items.some((item) => item.status === "error"),
    error,
    dragging,
    disabled,
    addFiles,
    remove,
    retry,
    clear,
    dismissError: () => setError(undefined),
    settle,
    handlePaste,
    dropZoneProps,
  };
}

/** A file that finished uploading before any composer existed, shaped as a ready tray item. */
export function uploadedAttachment(file: File, ref: AttachmentRef): PendingAttachment {
  return {
    key: `seed-${ref.id}`,
    file,
    mediaType: ref.mediaType,
    previewUrl: URL.createObjectURL(file),
    status: "ready",
    ref,
  };
}

function readyRefs(items: PendingAttachment[]): AttachmentRef[] {
  return items.flatMap((item) => (item.ref ? [item.ref] : []));
}

/** Intrinsic pixels for the upload's layout hints — advisory, so a decode
 * failure just drops them rather than failing the upload. */
function measureImage(url: string): Promise<{ width: number; height: number } | undefined> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(
      image.naturalWidth > 0 && image.naturalHeight > 0
        ? { width: image.naturalWidth, height: image.naturalHeight }
        : undefined,
    );
    image.onerror = () => resolve(undefined);
    image.src = url;
  });
}

function uploadErrorText(cause: unknown): string {
  if (cause instanceof RivetsApiError) return cause.message;
  return "Upload failed.";
}
